import { useState } from "react";
import { ChevronDown, ChevronRight, Plus, Trash2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useWizard } from "../../context/WizardContext";
import { CurrencyInput } from "../shared/CurrencyInput";
import type { EmployerDetail, EmployerInfo } from "../../types/individual.types";

function getEmployerDetail(list: EmployerDetail[] | undefined, personType: "TAXPAYER" | "SPOUSE"): EmployerDetail | undefined {
  if (!list) return undefined;
  return list.find((d) => d.personType === personType);
}

export function IndividualStep4Employer() {
  const { individualData, updateIndividualData } = useWizard();
  const list = individualData.employerDetails ?? [];
  const hasSpouse = (individualData.personalInformation ?? []).some((p) => p.personType === "SPOUSE");

  const updateDetail = (personType: "TAXPAYER" | "SPOUSE", patch: Partial<EmployerDetail>) => {
    const rest = list.filter((d) => d.personType !== personType);
    const existing = list.find((d) => d.personType === personType);
    const next: EmployerDetail = { ...existing, personType, ...patch };
    updateIndividualData({ employerDetails: [...rest, next] });
  };

  const setEmployerInfo = (personType: "TAXPAYER" | "SPOUSE", index: number, patch: Partial<EmployerInfo>) => {
    const detail = getEmployerDetail(list, personType) ?? { personType, employerInfo: [] };
    const info = [...(detail.employerInfo ?? [])];
    if (!info[index]) info[index] = {};
    info[index] = { ...info[index], ...patch };
    updateDetail(personType, { ...detail, employerInfo: info });
  };

  const addEmployer = (personType: "TAXPAYER" | "SPOUSE") => {
    const detail = getEmployerDetail(list, personType) ?? { personType, employerInfo: [] };
    const info = [...(detail.employerInfo ?? []), {}];
    updateDetail(personType, { ...detail, employerInfo: info });
  };

  const removeEmployer = (personType: "TAXPAYER" | "SPOUSE", index: number) => {
    const detail = getEmployerDetail(list, personType) ?? { personType, employerInfo: [] };
    const info = (detail.employerInfo ?? []).filter((_, i) => i !== index);
    updateDetail(personType, { ...detail, employerInfo: info });
  };

  const taxpayerDetail = getEmployerDetail(list, "TAXPAYER") ?? { personType: "TAXPAYER" as const, employerInfo: [] };
  const spouseDetail = getEmployerDetail(list, "SPOUSE") ?? { personType: "SPOUSE" as const, employerInfo: [] };
  const taxpayerInfo = taxpayerDetail.employerInfo ?? [];
  const spouseInfo = spouseDetail.employerInfo ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-foreground">Employer Details</h2>
        <p className="text-sm text-muted-foreground">
          W-2 information for each employer. Add multiple employers if needed.
        </p>
      </div>

      <Tabs defaultValue="taxpayer" className="w-full">
        <TabsList className="border border-primary/30 bg-primary/5">
          <TabsTrigger value="taxpayer" className="data-[state=active]:bg-black data-[state=active]:text-white data-[state=active]:font-semibold data-[state=active]:shadow-sm data-[state=active]:border-black">
            Taxpayer
          </TabsTrigger>
          {hasSpouse && (
            <TabsTrigger value="spouse" className="data-[state=active]:bg-black data-[state=active]:text-white data-[state=active]:font-semibold data-[state=active]:shadow-sm data-[state=active]:border-black">
              Spouse
            </TabsTrigger>
          )}
        </TabsList>
        <TabsContent value="taxpayer" className="mt-4 space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <Label className="w-full shrink-0">Employers</Label>
            <Button type="button" variant="outline" size="sm" onClick={() => addEmployer("TAXPAYER")} className="gap-1.5">
              <Plus className="h-4 w-4" />
              Add employer
            </Button>
          </div>
          {taxpayerInfo.map((info, i) => (
            <EmployerBlock
              key={i}
              index={i}
              info={info}
              setInfo={(patch) => setEmployerInfo("TAXPAYER", i, patch)}
              onRemove={() => removeEmployer("TAXPAYER", i)}
            />
          ))}
        </TabsContent>
        {hasSpouse && (
          <TabsContent value="spouse" className="mt-4 space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <Label className="w-full shrink-0">Employers</Label>
              <Button type="button" variant="outline" size="sm" onClick={() => addEmployer("SPOUSE")} className="gap-1.5">
                <Plus className="h-4 w-4" />
                Add employer
              </Button>
            </div>
            {spouseInfo.map((info, i) => (
              <EmployerBlock
                key={i}
                index={i}
                info={info}
                setInfo={(patch) => setEmployerInfo("SPOUSE", i, patch)}
                onRemove={() => removeEmployer("SPOUSE", i)}
              />
            ))}
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function EmployerBlock({
  index,
  info,
  setInfo,
  onRemove,
}: {
  index: number;
  info: EmployerInfo | undefined;
  setInfo: (patch: Partial<EmployerInfo>) => void;
  onRemove: () => void;
}) {
  const [open, setOpen] = useState(true);
  const w2 = info?.w2Income ?? {};

  return (
    <Card>
      <CardHeader
        className="cursor-pointer py-3 flex flex-row items-center justify-between gap-2"
        onClick={() => setOpen(!open)}
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {open ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
          )}
          <CardTitle className="text-sm truncate">
            Employer {index + 1} {info?.employerName ? `— ${info.employerName}` : ""}
          </CardTitle>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="text-destructive hover:text-destructive hover:bg-destructive/10 shrink-0"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
        >
          <Trash2 className="h-4 w-4 mr-1" />
          Remove
        </Button>
      </CardHeader>
      {open && (
        <CardContent className="space-y-4 pt-0">
          <div className="space-y-2">
            <Label>Employer name</Label>
            <Input
              value={info?.employerName ?? ""}
              onChange={(e) => setInfo({ employerName: e.target.value })}
              placeholder="Employer name"
            />
          </div>
          <div className="space-y-2">
            <Label>EIN</Label>
            <Input
              value={info?.ein ?? ""}
              onChange={(e) => setInfo({ ein: e.target.value })}
              placeholder="XX-XXXXXXX"
            />
          </div>
          <CurrencyInput
            label="Wages (Box 1)"
            value={w2.wages}
            onChange={(v) => setInfo({ w2Income: { ...w2, wages: v } })}
          />
          <CurrencyInput
            label="Federal tax withheld (Box 2)"
            value={w2.federalTaxWithheld}
            onChange={(v) => setInfo({ w2Income: { ...w2, federalTaxWithheld: v } })}
          />
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>State</Label>
              <Input
                value={info?.state ?? ""}
                onChange={(e) => setInfo({ ...info, state: e.target.value })}
                placeholder="State"
              />
            </div>
            <CurrencyInput
              label="State tax withheld"
              value={info?.stateTaxWithheld}
              onChange={(v) => setInfo({ ...info, stateTaxWithheld: v })}
            />
          </div>
        </CardContent>
      )}
    </Card>
  );
}
